import { useQuery } from '@tanstack/vue-query'
import { computed, watch } from 'vue'

import type { PjazzaTab } from '@/domains/pjazza/types/models/PjazzaTab.types.ts'

import { callPjazzaTabs } from '@/domains/pjazza/services'
import { useTabStore } from '@/domains/pjazza/store/use-tab-store/useTabStore.ts'
import { isTabGone } from '@/domains/pjazza/utils/isTabGone.ts'

/**
 * Keeps the table's running bill current while a diner is looking at it.
 *
 * Two cadences, because two screens want different things. The tab page polls
 * every 15s — slower than the order-status poll's 4s, since nobody is waiting
 * on a single decision, they are eating — and it is the screen people stare at
 * while waiting. The menu only refetches on focus: four diners polling for the
 * length of a meal is sixteen requests a minute for one table, and you pick
 * your phone up to order, so it is current by the time you look at it.
 *
 * Stops when the tab disappears — a settled bill returns 404, which is the
 * signal to forget the table rather than an error worth showing.
 */
export const useTabPolling = (siteSlug: string, options: { poll?: boolean } = {}) => {
  const tabStore = useTabStore()

  const sessionToken = computed(() => tabStore.tokenFor(siteSlug))

  const query = useQuery({
    queryKey: ['pjazza', 'tab', siteSlug, sessionToken],
    enabled: computed(() => sessionToken.value !== null),
    queryFn: async (): Promise<PjazzaTab | null> => {
      if (!sessionToken.value) return null

      const response = await callPjazzaTabs().show({ siteSlug, sessionToken: sessionToken.value })

      return response.data.value?.data ?? null
    },
    refetchInterval: options.poll === false ? false : 15_000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    // Gone means settled, not broken — no point retrying it.
    retry: (failureCount, error) => !isTabGone(error) && failureCount < 2
  })

  // The venue closed the tab: drop the table so the basket stops offering
  // to send rounds to a bill that no longer exists.
  watch(query.error, error => {
    if (error && isTabGone(error)) tabStore.leave()
  })

  const tab = computed(() => query.data.value ?? null)

  return {
    tab,
    isLoading: query.isPending,
    hasClosed: computed(() => query.error.value !== null && isTabGone(query.error.value)),
    refresh: query.refetch
  }
}
